import { ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"

import { Box } from "../../components/shared/Layout"

import SectionRenderer from "./SectionRenderer"
import { useWebsiteBuilderV2 } from "./WebsiteBuilderV2Context"

import "../../components/shared/wysiwyg.sass"

const WebsitePreviewViewV2 = ({ children }) => {
  const { practice, currentPage, sections, theme, previewTheme, previewSections, highlightedField } =
    useWebsiteBuilderV2()

  // Use preview theme if available, otherwise use the stored theme
  const activeTheme = previewTheme || theme
  // Use preview sections if available, otherwise use the stored sections
  const activeSections = previewSections || sections
  const [viewMode, setViewMode] = useState("desktop")

  const viewportWidth = window.innerWidth

  const scale = (viewportWidth - 696) / viewportWidth
  const invertScale = 1 / scale
  const contentStyles = {
    width: `calc((100vw - 696px) / ${scale})`,
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    height: `calc(100vh*${invertScale})`,
    overflow: "auto"
  }
  const wrapperStyles = {
    width: `calc((100vw - 696px))`,
    height: `calc(100vh)`,
    overflow: "hidden"
  }

  const renderPage = () => {
    if (!currentPage || !activeSections.length) {
      return (
        <div className="flex h-full items-center justify-center bg-gray-50">
          <div className="text-center">
            <h2 className="mb-4 text-2xl font-bold text-gray-600">
              {!currentPage ? "No page selected" : "No sections found"}
            </h2>
            <p className="text-gray-500">
              {!currentPage
                ? "Select a page from the Pages tab to start editing"
                : "Add sections to this page to see your content here"}
            </p>
          </div>
        </div>
      )
    }

    // Check if the first section is a HeroSection or SubHeroSection with full_image layout
    const firstSection = activeSections.find((section) => !section.hidden)
    const shouldHideHeader =
      (firstSection?.layout === "hero" || firstSection?.layout === "sub_hero") &&
      firstSection?.settings?.layout === "full_image"

    return (
      <main className="min-h-screen bg-white">
        {/* Header Section - Hide if first section is HeroSection or SubHeroSection with full_image layout */}
        {!shouldHideHeader && (
          <SectionRenderer
            section={{ id: "header-section", layout: "header" }}
            practice={practice}
            theme={activeTheme}
            highlightedField={highlightedField}
            viewingInBuilder={true}
            viewMode={viewMode}
          />
        )}

        {/* Page Sections */}
        {activeSections
          .filter((section) => !section.hidden) // Filter out hidden sections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <SectionRenderer
              key={section.id}
              section={section}
              practice={practice}
              theme={activeTheme}
              highlightedField={highlightedField}
              viewingInBuilder={true}
              pageSlug={currentPage?.slug}
              viewMode={viewMode}
            />
          ))}

        {/* Footer Section - Always show */}
        <SectionRenderer
          section={{ id: "footer-section", layout: "footer" }}
          practice={practice}
          theme={activeTheme}
          highlightedField={highlightedField}
          viewingInBuilder={true}
          viewMode={viewMode}
        />
      </main>
    )
  }

  const renderMobilePreview = () => {
    if (!currentPage) {
      return (
        <div className="flex h-full items-center justify-center">
          <Box className="bg-orange-light p-6">No page selected for mobile preview</Box>
        </div>
      )
    }

    return (
      <div className="relative flex h-[calc(100vh-72px)] w-full flex-col items-center bg-gray-ultralight">
        <div className="h-[844px] w-[390px] overflow-hidden rounded-lg bg-white shadow-lg">
          <div className="h-full w-full overflow-auto">{renderPage()}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex justify-between">
      <style>
        {activeTheme &&
          `
          .font-paragraph {
            font-family: ${activeTheme.fonts.paragraph} !important;
            font-weight: ${activeTheme.fonts.paragraphRegularWeight} !important;
          }
          .font-heading {
            font-family: ${activeTheme.fonts.heading} !important;
            font-weight: ${activeTheme.fonts.headingWeight} !important;
          }
        `}
      </style>

      {/* Desktop/Mobile Toggle */}
      <div className="fixed left-[200px] right-[496px] top-[34px] z-30 flex items-center justify-center md:hidden">
        <button
          className={`h-10 px-2 pb-4 ${viewMode === "desktop" ? "text-gray" : "border-b-4 border-black text-black"}`}
          onClick={() => setViewMode("mobile")}>
          <DevicePhoneMobileIcon className="h-6 w-6" />
        </button>
        <button
          className={`h-10 px-2 pb-4 ${viewMode === "mobile" ? "text-gray" : "border-b-4 border-black text-black"}`}
          onClick={() => setViewMode("desktop")}>
          <ComputerDesktopIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Preview Area */}
      <div className="font-paragraph overflow-hidden md:hidden" style={wrapperStyles}>
        {viewMode === "desktop" ? <div style={contentStyles}>{renderPage()}</div> : renderMobilePreview()}
      </div>

      {/* Sidebar */}
      <div className="fixed right-0 h-screen min-h-100 w-[496px] flex-none overflow-y-scroll border-l border-gray-light bg-white pb-24 md:w-full">
        {children}
      </div>
    </div>
  )
}

export default WebsitePreviewViewV2
